// The machine a terminal write came from (pure: no React, no DOM, no db).
//
// `npx innernet` reads what it is running on once (apps/cli/src/lib/device.ts)
// and sends it with every call as `x-innernet-machine: <kind>; <url-encoded name>`.
// When the door would otherwise say "CLI", the activity row is written as
// `device:<kind>:<name>` instead, so the river says "MacBook Pro" beside a
// laptop's outline. A label for the record, never authority: the paired key is
// still the only credential, and a header that does not parse is ignored.
//
// Not lib/device.ts: that one is the browser's device family (mobile / tablet /
// desktop) for the lifecycle notes; this one is the machine a key lives on.

export const MACHINE_KINDS = ['laptop', 'desktop', 'mini', 'studio', 'tower', 'server'] as const
export type MachineKind = (typeof MACHINE_KINDS)[number]

export interface Machine {
  kind: MachineKind
  /** the model, as its maker names it: "MacBook Pro", "ThinkPad X1 Carbon Gen 9" */
  name: string
}

const isKind = (v: unknown): v is MachineKind => typeof v === 'string' && (MACHINE_KINDS as readonly string[]).includes(v)

/** the same rule the cli keeps: letters, numbers, a few marks, 40 characters at most */
export function cleanMachineName(raw: unknown): string {
  return String(raw ?? '')
    .replace(/[^\p{L}\p{N} ()._+-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40)
}

/** `laptop; MacBook%20Pro` → { kind, name }, or null for anything else */
export function parseMachineHeader(raw: string | null | undefined): Machine | null {
  const m = /^\s*([a-z]+)\s*;\s*(\S.*)$/i.exec(String(raw ?? '').slice(0, 200))
  if (!m) return null
  const kind = m[1].toLowerCase()
  if (!isKind(kind)) return null
  let name: string
  try {
    name = decodeURIComponent(m[2])
  } catch {
    return null
  }
  name = cleanMachineName(name)
  return name ? { kind, name } : null
}

const SOURCE_RE = /^device:([a-z]+):(\S.*)$/i

/** the `activity_events.source` a machine's write carries */
export const machineSource = (m: Machine): string => `device:${m.kind}:${m.name}`

/** the machine a source names, or null when it names something else */
export function machineOfSource(raw: string | null | undefined): Machine | null {
  const m = SOURCE_RE.exec(String(raw ?? '').trim())
  if (!m) return null
  const kind = m[1].toLowerCase()
  const name = cleanMachineName(m[2])
  return name ? { kind: isKind(kind) ? kind : 'desktop', name } : null
}
